import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ActionFilter from '../../components/ActionFilter';
import dayjs from 'dayjs';
import CommonPagination from '../../components/Table/CommonPagination';
import AddIcon from '@mui/icons-material/Add';
import NotificationForm from './NotificationForm';
import AxiosInstance from '../../services/networkServices';
import Spinner from '../../components/Spinner';
import PageLayout from '../../components/PageLayout';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 'calc(100vh - 205px)',
    maxHeight: 'calc(100vh - 205px)',
    boxShadow: 'none !important',
  },
  tableCell: {
    fontSize: {
      xs: '14px !important',
      sm: '15px !important',
      md: '15px !important',
      lg: '16px !important',
      xl: '16px !important',
    },
    color: '#000 !important',
    fontWeight: '600 !important',
    padding: {
      xs: '4px 5px !important',
      sm: '6px 8px !important',
      md: '6px 8px !important',
      lg: '8px 10px !important',
      xl: '8px 10px !important',
    },
    backgroundColor: '#e0e0e0',
  },
  tableBody: {
    fontSize: {
      xs: '12px !important',
      sm: '13px !important',
      md: '14px !important',
      lg: '15px !important',
      xl: '15px !important',
    },
    color: '#333 !important',
    padding: {
      xs: '10px 5px !important',
      sm: '10px !important',
      md: '10px !important',
      lg: '10px 15px !important',
      xl: '10px 15px !important',
    },
  },
  table: {
    border: '1px solid rgba(224, 224, 224, 1)',
    minWidth: '100%',
    minHeight: '100%',
  },
  stickyHeader: {
    position: 'sticky',
    top: '-1px',
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '15px 0',
  },
}));

const ManageNotifications = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);

  const isDeleteDisabled =
    notificationToDelete?.status === 'SENT' ||
    notificationToDelete?.status === 'CANCELED';

  // Fetch notifications data
  useEffect(() => {
    fetchNotifications();
  }, [page]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get('/admin/admin-notifications');
      setNotifications(response.data.data);
      setTotalCount(response.data.data.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleAddNotification = () => {
    setSelectedNotification(null);
    setFormOpen(true);
  };

  const handleEditNotification = (notification) => {
    setSelectedNotification(notification);
    setFormOpen(true);
  };

  const handleDeleteNotification = (notification) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await AxiosInstance.delete(
        `/admin/admin-notification/${notificationToDelete._id}`
      );
      if (response.status === 200) {
        fetchNotifications();
        setDeleteDialogOpen(false);
        setNotificationToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setNotificationToDelete(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (selectedNotification) {
        await AxiosInstance.put(
          `/admin/admin-notification/${selectedNotification._id}`,
          {
            ...formData,
            sendDate: formData.sendDate.format('YYYY-MM-DDTHH:mm:ssZ'),
            sendTo: 'ALL',
          }
        );
      } else {
        await AxiosInstance.post('/admin/admin-notification', {
          ...formData,
          sendDate: formData.sendDate.format('YYYY-MM-DDTHH:mm:ssZ'),
        });
      }
      fetchNotifications();
    } catch (error) {
      console.error('Error saving notification:', error);
    }
  };

  const handleCloseDialog = () => {
    setSelectedNotification(null);
    setFormOpen(false);
  };

  return (
    <PageLayout
      title="Notifications"
      loading={loading}
      actions={
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddNotification}
          startIcon={<AddIcon />}
        >
          Add Notification
        </Button>
      }
    >
      <Box className={classes.root}>
        <Box
          sx={{
            padding: '15px 15px 0 15px',
            borderRadius: '6px',
            boxShadow:
              'rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px',
          }}
        >
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell className={classes.tableCell}>Title</TableCell>
                  <TableCell className={classes.tableCell}>Content</TableCell>
                  <TableCell className={classes.tableCell}>Send Date</TableCell>
                  <TableCell className={classes.tableCell}>Status</TableCell>
                  <TableCell className={classes.tableCell}>Deleted</TableCell>
                  <TableCell className={classes.tableCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className={classes.tableBody}
                      align="center"
                    >
                      No notifications found
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((notification) => (
                    <TableRow key={notification._id}>
                      <TableCell className={classes.tableBody}>
                        {notification.title}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {notification.content}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {dayjs(notification.sendDate).format(
                          'ddd, MMM D, YYYY @ hh:mm A'
                        )}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {notification.status || 'Pending'}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        {notification.isDeleted ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell className={classes.tableBody}>
                        <ActionFilter
                          onEdit={() => handleEditNotification(notification)}
                          onDelete={() =>
                            handleDeleteNotification(notification)
                          }
                          index={notification._id}
                          notification
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <CommonPagination
            count={totalCount}
            rowsPerPage={10}
            page={page}
            onPageChange={handlePageChange}
          />
        </Box>

        {/* Delete Notification Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Notification</DialogTitle>
          <DialogContent>
            <Typography color="error">
              {!isDeleteDisabled
                ? `Are you sure you want to delete and cancel this scheduled notification?`
                : `This notification has already been ${
                    notificationToDelete?.status === 'SENT' ? 'sent' : 'deleted'
                  } and cannot be deleted/canceled`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={isDeleteDisabled}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add/Edit Notification Dialog */}
        <NotificationForm
          open={formOpen}
          onClose={handleCloseDialog}
          onSubmit={handleFormSubmit}
          initialData={selectedNotification}
        />
      </Box>
    </PageLayout>
  );
};

export default ManageNotifications;
